import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";

import {
  deleteProperty,
  fetchComplaintCategories,
  fetchPropertyDetail,
  logPropertyViewBestEffort,
  patchPropertyStatus,
  submitPropertyComplaint,
} from "../api/realty";

// R8.6 — Follow rejoin to the canonical Redux propagation path. Detail
// follow now dispatches the same thunk that CreateChatModal /
// AddMembersModal / UserScreen consume via selectFollowGraph. Imports
// reach app-global Redux primitives (actions/, constants/, selectors/),
// NOT another feature's internals — SR-6 (feature-isolation) still
// holds for the complaint wrappers that remain in ../api/realty.
import { followUser } from "../../../actions/userActions";
import { USER_FOLLOW_RESET } from "../../../constants/userConstants";
import { selectFollowGraph } from "../../../selectors/userSelectors";

import DetailHero from "./DetailHero";
import DetailGallery from "./DetailGallery";
import DetailQuickfacts from "./DetailQuickfacts";
import DetailDescription from "./DetailDescription";
import DetailLocation from "./DetailLocation";
import DetailSpecs from "./DetailSpecs";
import DetailFeatures from "./DetailFeatures";
import DetailStats from "./DetailStats";
import DetailOwnerCard from "./DetailOwnerCard";
import DetailActionsPanel from "./DetailActionsPanel";
import LegacyLocationUpgradeNotice from "../../geo/widgets/LegacyLocationUpgradeNotice";
import DetailActionModals from "./DetailActionModals";
import DetailCooperationBlock from "../../realty/widgets/DetailCooperationBlock";
import DealCreateFlowModal from "../../deals/widgets/DealCreateFlowModal";
import PropertyDealsSection from "./PropertyDealsSection";
import { getStatusSuccessMessage } from "../../realty/lib/propertyLifecycle";
import LandParcelsDetail from "../../landParcels/widgets/LandParcelsDetail";
import { buildOwnerContactPrefill } from "../../clientPresentation/lib/ownerContactPrefill";

// RealtyDetailScreenContent — /realty/:id canonical detail surface.
//
// Substrate: design/detail.html #Csection. Per R3.1 directive this
// widget restores the canonical 9-section topology with backend data
// binding but WITHOUT lifecycle orchestration.
//
// Explicitly NOT in R3.1:
//   * Splide mount (gallery)            — R3.3
//   * Bootstrap modal lifecycle (refs)  — R3.3 / R3.11
//   * Leaflet map mount                 — R3.6
//   * YouTube IFrame init               — R3.3
//   * Ownership derivation              — R3.9
//   * Engagement / mutation handlers    — R3.10 / R3.11
//
// Canonical detail.html renders BOTH "Робота з оголошенням" and
// "Реакції" groups statically — R3.1 mirrors that verbatim. R3.9 is
// the step that introduces owner-vs-viewer screen-level branching.

// PROPERTY-DETAIL-BLACK-SCREEN-QA-A1-R2 — feature-local initial-load
// mapping only. Do NOT replace shared messageFromError globally; mutation
// paths (status/delete/follow/complaint) keep messageFromError as before.
const PROPERTY_DETAIL_LOAD_ERROR_TITLE =
  "Не вдалося завантажити оголошення";

const mapPropertyDetailLoadError = (error) => {
  const status =
    error && error.response && typeof error.response.status === "number"
      ? error.response.status
      : null;

  if (status === null) {
    return "Не вдалося з'єднатися із сервером. Перевірте інтернет і спробуйте ще раз.";
  }
  if (status === 401) {
    return "Увійдіть, щоб переглянути це оголошення.";
  }
  if (status === 403) {
    return "У вас немає доступу до цього оголошення.";
  }
  if (status === 404) {
    return "Оголошення не знайдено або воно недоступне.";
  }
  if (status >= 500 && status <= 599) {
    return "Сервіс тимчасово недоступний. Спробуйте пізніше.";
  }
  return "Не вдалося завантажити оголошення. Спробуйте ще раз.";
};

function RealtyDetailScreenContent() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // R3.9 — Single auth read at screen level. Foundation-frozen
  // canonical path (state.userLogin.userInfo per
  // project-context/current-task.md). No selector inside any widget.
  const userInfo = useSelector(
    (state) => state.userLogin && state.userLogin.userInfo,
  );

  // R8.6 — Canonical follow truth via memoized selectFollowGraph (the
  // same seam consumed by CreateChatModal, AddMembersModal, UserScreen).
  // state.userFollow is consumed below for pending-teardown ONLY — it is
  // never read as a truth source for the follow state of the rendered
  // owner. The two ownerships (Redux=truth, local isFollowing=pending)
  // remain orthogonal.
  const { following: followingList } = useSelector(selectFollowGraph);
  const userFollow = useSelector((state) => state.userFollow);

  const [property, setProperty] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);

  // R3.11A — Destructive mutation state. Screen-local; not lifted to
  // any store. Two independent pending booleans so a failed sold
  // attempt cannot block a subsequent delete attempt (or vice-versa).
  // destructiveError surfaces below the action section; it is
  // intentionally separate from DetailActionsPanel's engagementError
  // — engagement and destructive failures have different ownership.
  const [isMutatingStatus, setIsMutatingStatus] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [destructiveError, setDestructiveError] = useState(null);

  const mutatingStatusRef = useRef(false);
  const deletingRef = useRef(false);

  // R8.6 — Follow ownership split.
  //
  //   following (truth)  → derived ONLY from selectFollowGraph below;
  //                        canonical Redux follow graph populated by
  //                        USER_LOGIN_SUCCESS at bootstrap/login and
  //                        re-merged on every followUser thunk dispatch.
  //   isFollowing        → screen-local pending lock. Gates duplicate
  //                        dispatch while the PUT is in flight; torn
  //                        down by the userFollow.success/error effect.
  //
  // The two ownerships never mirror each other. Pre-R8.6 this screen
  // kept a screen-local `following` boolean hydrated from
  // property.created_by.is_followed_by_me, which (a) bypassed canonical
  // truth propagation so chat modals stayed stale after a detail-page
  // follow, and (b) was always false on first render because
  // UserShortSerializer does not actually expose is_followed_by_me.
  // Both seams close together here.
  //
  // Complaint state is decomposed: categories are fetched once per
  // screen mount (for non-owner authenticated viewers); the form state
  // (selected category, description) lives inside DetailActionModals
  // since those are purely modal-local concerns. The screen owns
  // mutation execution + pending + error/success flags.
  const [isFollowing, setIsFollowing] = useState(false);
  const [actionError, setActionError] = useState(null);

  const [complaintCategories, setComplaintCategories] = useState([]);
  const [complaintCategoriesError, setComplaintCategoriesError] = useState(
    null,
  );
  const [isReportingComplaint, setIsReportingComplaint] = useState(false);
  const [complaintError, setComplaintError] = useState(null);
  const [complaintSuccess, setComplaintSuccess] = useState(false);

  const [proposeOpen, setProposeOpen] = useState(false);
  const [startInternalOpen, setStartInternalOpen] = useState(false);

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchPropertyDetail(id)
      .then((data) => {
        if (cancelled) return;
        setProperty(data);
        setLoadState("loaded");
        if (data) {
          Promise.resolve(logPropertyViewBestEffort(id)).catch(() => {});
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(mapPropertyDetailLoadError(err));
        setLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // R3.3B — Bootstrap modal residual cleanup.
  //
  // Bootstrap drives modal lifecycle imperatively via data-bs-*
  // attributes; when React unmounts a modal element mid-show
  // (e.g., user navigates while a modal is open, or transitions
  // PROP_A → PROP_B which clears the prior gallery DOM), Bootstrap
  // never fires `hide.bs.modal` and leaves three artifacts behind:
  //   * <body> retains the `modal-open` class
  //   * <body> retains inline `overflow: hidden` + `padding-right`
  //   * a `.modal-backdrop` sibling stays attached to <body>
  //
  // The cleanup runs on:
  //   * pathname change — covers /realty/1 → /realty/2 (component
  //     reuse) AND /realty/1 → /feed (route remount) since both
  //     cross pathname
  //   * RealtyDetailScreenContent unmount
  //
  // All three operations are idempotent — safe whether or not a
  // modal was actually open at the moment of navigation.
  //
  // CONTAINMENT: detail-feature-local. Not a modal manager. Not a
  // shared overlay cleanup. Other features owning modals MUST
  // contain their own cleanup at their own screen level.
  useEffect(() => {
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      document
        .querySelectorAll(".modal-backdrop")
        .forEach((el) => el.parentNode?.removeChild(el));
    };
  }, [pathname]);

  const handleStatusMutation = async (nextStatus) => {
    if (mutatingStatusRef.current || !property) return;
    mutatingStatusRef.current = true;
    setDestructiveError(null);
    setIsMutatingStatus(true);
    const previousStatus = property.status;
    try {
      const updated = await patchPropertyStatus(property.id, nextStatus);
      setProperty((prev) =>
        prev
          ? {
              ...prev,
              status: updated.status,
              is_moderation_locked:
                updated.is_moderation_locked ?? prev.is_moderation_locked,
            }
          : prev,
      );
      toast.success(
        getStatusSuccessMessage(nextStatus, {
          dealType: property.deal_type,
          fromStatus: previousStatus,
        }),
      );
    } catch (err) {
      console.error("RealtyDetailScreenContent: status mutation failed", err);
      setDestructiveError(messageFromError(err));
    } finally {
      mutatingStatusRef.current = false;
      setIsMutatingStatus(false);
    }
  };

  const handleConfirmPublish = () => handleStatusMutation("active");
  const handleConfirmUnpublish = () => handleStatusMutation("draft");
  const handleConfirmReserve = () => handleStatusMutation("reserved");
  const handleConfirmSold = () => handleStatusMutation("sold");
  const handleConfirmArchive = () => handleStatusMutation("archived");
  const handleConfirmReactivate = () => handleStatusMutation("active");

  // R3.11A — Delete mutation. Order is strict per directive:
  // confirm → await backend delete → navigate. The component unmounts
  // on navigate, so isDeleting=true intentionally persists through the
  // success path (no setIsDeleting(false) needed). On failure the
  // screen stays mounted — clear isDeleting so the user can retry.
  //
  // R8.5 Fix B — `replace: true` evicts /realty/:id from the history
  // stack so browser-back lands on the previous entry, not a 404.
  //
  // R8.7.4 — Replaced the prior `state.mayBeStale` navigation hint with
  // a success toast. Rationale: the destination /feed re-fetches on
  // mount; the deleted entity is genuinely gone from the backend's
  // result set; the prior "list may be stale" banner overclaimed
  // uncertainty. A toast states the actual event (deletion) without
  // making a false freshness claim. ToastContainer is mounted in
  // RootLayout; no new infrastructure introduced.
  const handleConfirmDelete = async () => {
    // R8.7 B-1 — synchronous ref guard closes the rapid-double-click
    // window before React re-renders the state-derived disable. On
    // success the component unmounts on navigate so the ref is
    // released only on the failure branch (mirrors setIsDeleting(false)
    // intent below).
    if (deletingRef.current || !property) return;
    deletingRef.current = true;
    setDestructiveError(null);
    setIsDeleting(true);
    try {
      await deleteProperty(property.id);
      toast.success("Оголошення видалено.");
      navigate("/feed", { replace: true });
    } catch (err) {
      console.error("RealtyDetailScreenContent: delete failed", err);
      setDestructiveError(messageFromError(err));
      deletingRef.current = false;
      setIsDeleting(false);
    }
  };

  // R3.12A — Complaint categories. Fetched once per screen mount for
  // authenticated non-owner viewers. Anonymous viewers and owners do
  // not consume the catalog (anonymous: would 401; owners: cannot file
  // complaints against own property — backend rejects with 400). The
  // result feeds the radio list inside DetailActionModals. Failures
  // are surfaced via complaintCategoriesError; the modal renders a
  // muted empty state in that case rather than crashing.
  const currentUserIdForFetch =
    (userInfo && (userInfo.id || userInfo.public_id)) || null;
  const ownerIdForFetch =
    (property &&
      property.created_by &&
      (property.created_by.id || property.created_by.public_id)) ||
    null;
  const shouldFetchCategories =
    currentUserIdForFetch !== null &&
    ownerIdForFetch !== null &&
    String(ownerIdForFetch) !== String(currentUserIdForFetch);

  useEffect(() => {
    if (!shouldFetchCategories) return undefined;
    let cancelled = false;
    fetchComplaintCategories()
      .then((data) => {
        if (cancelled) return;
        // SR-5: backend truth overrides design. Filter to categories
        // applicable to property complaints — exclude "user"-only rows.
        // "both" categories remain visible.
        const filtered = Array.isArray(data)
          ? data.filter((cat) => cat && cat.applies_to !== "user")
          : [];
        setComplaintCategories(filtered);
        setComplaintCategoriesError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(
          "RealtyDetailScreenContent: complaint categories fetch failed",
          err,
        );
        setComplaintCategoriesError(messageFromError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [shouldFetchCategories]);

  // R8.6 — Follow mutation rejoined to the canonical Redux propagation
  // path. PUT /api/users/profile/follow/ is dispatched as the
  // userActions.followUser thunk:
  //
  //   USER_FOLLOW_REQUEST
  //   → axios PUT /users/profile/follow/
  //   → USER_FOLLOW_SUCCESS
  //   → mergeUserInfo + USER_LOGIN_SUCCESS
  //   → persistUserInfo
  //   → selectFollowGraph recompute
  //   → CreateChatModal / AddMembersModal / UserScreen update
  //
  // `following` (boolean) is derived from selectFollowGraph keyed by
  // ownerPublicId. R3.9 still hides the trigger from anonymous viewers'
  // owner branch only when isOwner; anonymous viewers CAN attempt and
  // will see a 401 surfaced as actionError via the teardown effect.
  const ownerPublicId =
    (property &&
      property.created_by &&
      (property.created_by.public_id || null)) ||
    null;

  const following =
    !!ownerPublicId &&
    followingList.some((u) => u && u.public_id === ownerPublicId);

  // R8.6 — Follow pending-teardown. Gated by local `isFollowing` so a
  // stale userFollow.success/error left by a follow performed on
  // another surface (e.g. UserScreen mid-route) cannot tear down this
  // screen's pending lock. The symmetric race — stale residue tripping
  // the effect on the same tick we set isFollowing=true — is closed by
  // dispatching USER_FOLLOW_RESET inside handleConfirmFollow BEFORE the
  // thunk dispatch.
  const followSuccess = !!(userFollow && userFollow.success);
  const followError = userFollow && userFollow.error;
  useEffect(() => {
    if (!isFollowing) return;
    if (followSuccess) {
      setIsFollowing(false);
      setActionError(null);
      dispatch({ type: USER_FOLLOW_RESET });
    } else if (followError) {
      setIsFollowing(false);
      setActionError(
        typeof followError === "string" ? followError : String(followError),
      );
      dispatch({ type: USER_FOLLOW_RESET });
    }
  }, [isFollowing, followSuccess, followError, dispatch]);

  const handleConfirmFollow = () => {
    if (isFollowing || following || !ownerPublicId) return;
    setActionError(null);
    dispatch({ type: USER_FOLLOW_RESET });
    setIsFollowing(true);
    dispatch(followUser(ownerPublicId));
  };

  // R3.12A — Complaint submission. Fire-and-forget moderation input
  // per directive STEP R3.12A.4. The modal collects category + free
  // description; the screen handler executes the POST. Backend
  // enforces:
  //   * exactly one target (target_property_id supplied here)
  //   * own-property guard (returns 400)
  //   * block-guard via assert_can_interact (returns 400)
  // On success, complaintSuccess flips true so the modal can render an
  // inline confirmation; failures surface via complaintError inside
  // the modal body — local, ephemeral, non-orchestrated.
  const handleSubmitComplaint = async ({ categoryId, description }) => {
    if (isReportingComplaint || !property || !categoryId) return;
    setComplaintError(null);
    setComplaintSuccess(false);
    setIsReportingComplaint(true);
    try {
      await submitPropertyComplaint({
        categoryId,
        description,
        propertyId: property.id,
      });
      setComplaintSuccess(true);
    } catch (err) {
      console.error("RealtyDetailScreenContent: complaint submit failed", err);
      setComplaintError(messageFromError(err));
    } finally {
      setIsReportingComplaint(false);
    }
  };

  const handleDealCreateSuccess = (deal) => {
    setProposeOpen(false);
    setStartInternalOpen(false);
    if (deal && deal.id != null) {
      navigate(`/deals/${deal.id}`);
    }
  };

  if (loadState === "loading") {
    return (
      <section className="detail-section feature-realty-detail" id="Csection">
        <div className="container">
          <Spinner size="lg" label="Завантаження оголошення" />
        </div>
      </section>
    );
  }

  if (loadState === "error") {
    return (
      <section className="detail-section feature-realty-detail" id="Csection">
        <div className="container">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title={PROPERTY_DETAIL_LOAD_ERROR_TITLE}
            description={loadError || "Спробуйте оновити сторінку."}
            size="md"
          />
        </div>
      </section>
    );
  }

  if (!property) {
    // Defensive empty after load/error gates — keep #Csection so MainLayout
    // still has a content grid item (UI-LAYOUT-CSECTION-A1-R1).
    return (
      <section className="detail-section feature-realty-detail" id="Csection">
        <div className="container">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Оголошення недоступне"
            description="Не вдалося відобразити оголошення."
            size="md"
          />
        </div>
      </section>
    );
  }

  // R3.9 — Ownership derivation. Computed once, after fetch resolves.
  //
  // Pattern matched verbatim to RealtyHomeScreenContent.buildMenu
  // (the realtyList feature's own ownership predicate). String
  // coercion guards DRF's number-vs-string id serialization.
  //
  // canViewPrivateDescription gates on isOwner ONLY, NOT is_realtor.
  // The realtor predicate is unresolved (no `is_realtor` on User);
  // owner is the safe lower bound. When backend exposes a realtor
  // flag this expression widens — until then "all realtors" stays
  // structurally placeheld.
  const currentUserId =
    (userInfo && (userInfo.id || userInfo.public_id)) || null;
  const ownerId =
    property.created_by &&
    (property.created_by.id || property.created_by.public_id);
  const isOwner =
    currentUserId !== null &&
    ownerId !== undefined &&
    ownerId !== null &&
    String(ownerId) === String(currentUserId);
  const canContact = !isOwner;
  const canViewPrivateDescription = isOwner;

  return (
    <section
      className="detail-section feature-realty-detail"
      id="Csection"
      data-property-type={property.property_type}
      data-deal-type={property.deal_type}
      data-status={property.status}
    >
      {/* 1. Hero — shared (no ownership branching) */}
      <DetailHero property={property} />

      {/* 2. Gallery — shared */}
      <DetailGallery property={property} />

      {/* 3. Quickfacts + Programs — shared */}
      <DetailQuickfacts property={property} />

      {/* 3b. Cooperation offer — visible when API exposes it */}
      <DetailCooperationBlock property={property} isOwner={isOwner} />

      {/* 3c. D2-E1 — party-filtered reverse Deals (authenticated only) */}
      {userInfo ? <PropertyDealsSection propertyId={property.id} /> : null}

      {/* 4. Description — private body gated by R3.9 derivation */}
      <DetailDescription
        property={property}
        canViewPrivateDescription={canViewPrivateDescription}
      />

      {/* 5. Location — shared */}
      <DetailLocation property={property} />

      {/* 6. Specs — shared */}
      <DetailSpecs property={property} />

      <LandParcelsDetail parcels={property.land_parcels} />

      {/* 7. Features — shared */}
      <DetailFeatures property={property} />

      {/* 8. Stats — shared, snapshot only */}
      <DetailStats property={property} />

      {/* 9. Owner card + Actions panel — branches on R3.9 derivation */}
      <section className="detail-owner-actions">
        <div className="container">
          {isOwner ? (
            <LegacyLocationUpgradeNotice
              propertyId={property.id}
              location={property.location}
              variant="full"
            />
          ) : null}
          {destructiveError ? (
            <p className="detail-owner-actions__error" role="alert">
              {destructiveError}
            </p>
          ) : null}
          {actionError ? (
            <p className="detail-owner-actions__error" role="alert">
              {actionError}
            </p>
          ) : null}
          <div className="row gx-4 gy-4 detail-page-align-row detail-owner-actions__grid">
            <div className="col-12 col-lg-5">
              <DetailOwnerCard
                property={property}
                canContact={canContact}
                isAuthenticated={Boolean(userInfo)}
              />
            </div>
            <div className="col-12 col-lg-7">
              <DetailActionsPanel
                property={property}
                isOwner={isOwner}
                isAuthenticated={Boolean(userInfo)}
                following={following}
                isFollowing={isFollowing}
                isMutatingStatus={isMutatingStatus}
                onProposeClient={() => setProposeOpen(true)}
                onStartInternalDeal={() => setStartInternalOpen(true)}
                ownerContactPrefill={buildOwnerContactPrefill(userInfo)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Action modals — R3.11A wired sold/delete (owner pair).
          R3.12A wires follow + complaint (viewer pair). Each pair
          receives only the handlers/flags relevant to the action
          group rendered above. */}
      <DetailActionModals
        isOwner={isOwner}
        property={property}
        onConfirmPublish={handleConfirmPublish}
        onConfirmUnpublish={handleConfirmUnpublish}
        onConfirmReserve={handleConfirmReserve}
        onConfirmSold={handleConfirmSold}
        onConfirmArchive={handleConfirmArchive}
        onConfirmReactivate={handleConfirmReactivate}
        onConfirmDelete={handleConfirmDelete}
        isMutatingStatus={isMutatingStatus}
        isDeleting={isDeleting}
        onConfirmFollow={handleConfirmFollow}
        isFollowingPending={isFollowing}
        followAlreadyFollowing={following}
        complaintCategories={complaintCategories}
        complaintCategoriesError={complaintCategoriesError}
        onSubmitComplaint={handleSubmitComplaint}
        isReportingComplaint={isReportingComplaint}
        complaintError={complaintError}
        complaintSuccess={complaintSuccess}
      />

      <DealCreateFlowModal
        open={proposeOpen}
        mode="propose"
        property={property}
        onClose={() => setProposeOpen(false)}
        onSuccess={handleDealCreateSuccess}
      />
      <DealCreateFlowModal
        open={startInternalOpen}
        mode="start-internal"
        property={property}
        onClose={() => setStartInternalOpen(false)}
        onSuccess={handleDealCreateSuccess}
      />
    </section>
  );
}

export default RealtyDetailScreenContent;
